import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { parseAudioPointId } from '~/training/sequence/templates/items/image-map2/audioPointIdUtils';

const PopupContainer = styled.div`
  position: absolute;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 1000;
  min-width: 120px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  text-align: left;

  &:hover {
    background: #f5f5f5;
  }

  &:active {
    background: #e0e0e0;
  }
`;

const EditIconForButton = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const TrashIconForButton = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

interface AudioPointPopupMenuProps {
  isOpen: boolean;
  anchorElement: HTMLElement | null;
  onClose: () => void;
  audioPointId: string;
  onEdit: () => void;
  onDelete?: () => void;
  constrainToViewport?: boolean;
}

const AudioPointPopupMenu: React.FC<AudioPointPopupMenuProps> = ({
  isOpen,
  anchorElement,
  onClose,
  audioPointId,
  onEdit,
  onDelete,
  constrainToViewport = false
}) => {
  const { removeSubItem } = useEditorStore();
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && anchorElement && !constrainToViewport) {
      const rect = anchorElement.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.right + window.scrollX - 120
      });
    }
  }, [isOpen, anchorElement, constrainToViewport]);

  useLayoutEffect(() => {
    if (!isOpen || !anchorElement || !constrainToViewport) return;
    const place = () => {
      const menu = popupRef.current;
      if (!menu) return;
      const anchor = anchorElement.getBoundingClientRect();
      const bounds = menu.getBoundingClientRect();
      const below = anchor.bottom + 5;
      const top =
        below + bounds.height <= window.innerHeight - 8
          ? below
          : anchor.top - bounds.height - 5;
      setPosition({
        top: Math.max(8, Math.min(top, window.innerHeight - bounds.height - 8)),
        left: Math.max(
          8,
          Math.min(
            anchor.right - bounds.width,
            window.innerWidth - bounds.width - 8
          )
        )
      });
    };
    place();
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    if (popupRef.current) observer?.observe(popupRef.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen, anchorElement, constrainToViewport]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleEdit = () => {
    onEdit();
    onClose();
  };

  const handleDelete = () => {
    // Parse the audio point ID using the utility function
    const idResult = parseAudioPointId(audioPointId);

    // Template string IDs (like 'audio-50-75') don't have backend records yet
    if (idResult.isTemporary) {
      console.warn(
        'Attempting to delete audio point with template ID - no backend record exists:',
        idResult.originalId
      );
      // For template IDs, call the onDelete callback if provided
      // This allows the parent component to handle temporary audio point removal
      if (onDelete) {
        onDelete();
      }
      onClose();
      return;
    }

    // Handle real backend IDs
    if (idResult.isValid && idResult.numericId !== null) {
      removeSubItem(idResult.numericId);
    } else {
      console.error(
        'Invalid audio point ID for deletion:',
        idResult.originalId
      );
    }

    onClose();
  };

  if (!isOpen || !anchorElement) {
    return null;
  }

  return ReactDOM.createPortal(
    <PopupContainer
      ref={popupRef}
      style={{
        top: position.top,
        left: position.left,
        ...(constrainToViewport
          ? {
              position: 'fixed',
              maxWidth: 'calc(100vw - 16px)',
              maxHeight: 'calc(100vh - 16px)',
              overflowY: 'auto'
            }
          : {})
      }}
    >
      <Button onClick={handleEdit}>
        <EditIconForButton /> Edit
      </Button>
      <Button onClick={handleDelete}>
        <TrashIconForButton /> Delete
      </Button>
    </PopupContainer>,
    document.body
  );
};

export default AudioPointPopupMenu;
